import type { HoursInfo, StoreResponse } from './StoreLocator.types'

/**
 * Maps JS Date.getDay() index (0 = Sunday) to the correct API field name.
 * The API uses "{day}TbaC" fields with "HH:MM - HH:MM" values.
 */
const DAY_KEYS: Array<keyof StoreResponse> = [
  'sundayTbaC',
  'mondayTbaC',
  'tuesdayTbaC',
  'wednesdayTbaC',
  'thursdayTbaC',
  'fridayTbaC',
  'saturdayTbaC',
]

const TIME_RANGE_RE = /(\d{1,2}):(\d{2})\s*[-–]\s*(\d{1,2}):(\d{2})/

function getTodayHours(store: StoreResponse): string {
  const key = DAY_KEYS[new Date().getDay()] as keyof StoreResponse
  return (store[key] as string | null) ?? ''
}

function toMinutes(h: string, m: string): number {
  return parseInt(h, 10) * 60 + parseInt(m, 10)
}

/**
 * Returns structured hours info for today.
 * The caller is responsible for passing `info.key` (and `info.time`) to `t()`
 * so that the label is rendered in the user's language.
 *
 * Returns null only when today's hours string is unparseable.
 *
 * status:
 *   "open"        → store is currently open  (show green)
 *   "opens-later" → store opens later today  (show text-secondary)
 *   "closed"      → store is closed today    (show red)
 */
export function getStoreHoursInfo(store: StoreResponse): HoursInfo | null {
  const hours = getTodayHours(store)

  if (
    !hours ||
    hours.toLowerCase().trim() === 'closed' ||
    hours.trim() === '-'
  ) {
    return { key: 'storeLocator.closedToday', status: 'closed' }
  }

  const match = hours.match(TIME_RANGE_RE)
  if (!match) return null

  const [, openH, openM, closeH, closeM] = match
  const openMinutes = toMinutes(openH!, openM!)
  const closeMinutes = toMinutes(closeH!, closeM!)
  const nowMinutes = new Date().getHours() * 60 + new Date().getMinutes()

  if (nowMinutes < openMinutes) {
    return {
      key: 'storeLocator.opensAt',
      time: `${openH}:${openM}`,
      status: 'opens-later',
    }
  }
  if (nowMinutes < closeMinutes) {
    return {
      key: 'storeLocator.openUntil',
      time: `${closeH}:${closeM}`,
      status: 'open',
    }
  }
  return { key: 'storeLocator.closedToday', status: 'closed' }
}

/**
 * Formats the store address as two lines:
 *   line 1: street address (address1C)
 *   line 2: zip + city
 */
export function formatStoreAddress(store: StoreResponse): {
  line1: string | null
  line2: string
} {
  const line2Parts = [store.zipC, store.cityC].filter(Boolean)
  return {
    line1: store.address1C,
    line2: line2Parts.join(' '),
  }
}
